import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToastContext } from '@/contexts/ToastContext';
import { authService } from '@/api/services';
import { User, Loader2, Phone, MapPin } from 'lucide-react';
import { CollegeProgramSelector } from '@/components/CollegeProgramSelector';

interface GoogleRegistrationData {
  phone_number: string;
  program: string;
  company: string;
}

const GoogleRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastContext();
  const [loading, setLoading] = useState(false);
  
  // Get Google user data from URL params
  const email = searchParams.get('email') || '';
  const firstName = searchParams.get('first_name') || '';
  const lastName = searchParams.get('last_name') || '';
  const picture = searchParams.get('picture') || '';
  
  const { register, handleSubmit, formState: { errors } } = useForm<GoogleRegistrationData>();
  const [program, setProgram] = useState('');

  const onSubmit = async (data: GoogleRegistrationData) => {
    setLoading(true);
    try {
      const response = await authService.googleCompleteRegistration({
        email,
        first_name: firstName,
        last_name: lastName,
        picture: picture,
        phone_number: data.phone_number,
        program: program,
        department: '', // Will be derived from program
        company_name: data.company,
        company_region: '',
        student_id: '',
        year_of_study: '',
        pt_phase: 'PT1',
      });
      
      if (response.success) {
        // Store tokens
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        
        showSuccess('Registration completed successfully!');
        navigate('/dashboard');
      } else {
        showError(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Google registration error:', error);
      showError(error?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {/* Header with Google Profile */}
          <div className="text-center mb-8">
            {picture && (
              <div className="mx-auto mb-4">
                <img 
                  src={picture} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full mx-auto border-4 border-orange-200 dark:border-orange-700"
                />
              </div>
            )}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome, {firstName}!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Just a few more details to get started
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </div>
              </label>
              <input
                type="tel"
                {...register('phone_number', { 
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[+]?[\d\s-()]+$/,
                    message: 'Invalid phone number format'
                  }
                })}
                placeholder="+254 7XX XXX XXX"
                className="input-field"
              />
              {errors.phone_number && (
                <p className="mt-1 text-sm text-red-600">{errors.phone_number.message}</p>
              )}
            </div>

            {/* College and Program Selection */}
            <div>
              <CollegeProgramSelector 
                value={program} 
                onChange={setProgram} 
                required={true} 
              />
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Company (Where you're doing attachment)
                </div>
              </label>
              <input
                type="text"
                {...register('company', { required: 'Company is required' })}
                placeholder="e.g., Safaricom PLC"
                className="input-field"
              />
              {errors.company && (
                <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <User className="w-5 h-5" />
                  Complete Registration
                </>
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleRegistrationPage;
